import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { CUSTOM_MODEL_NUMBERS_QUERY_KEY } from "hooks/api/customModelNumbers/customModelNumbers.hook"
import { useQuotationPeerSyncStore } from "store/quotationPeerSync.store"
import AnnotationService from "services/annotations.service"
import type { TQuotationDetail } from "types/quotations.type"
import type { TCoordinates, TEcall, TEref, TFurniture } from "types/elevationReferences.type"

// Cross-window cache sync for the quotation editor's pop-out window (#1430, requirement 3).
//
// The pop-out is a REAL browser window (`window.open` → our own `/quotation-window` route), which
// is the only way a view can be dragged onto another monitor. That means it boots a second copy of
// the app: its own document, its own JS realm, its own module registry — and therefore its own
// TanStack cache. It is NOT the shared-cache model the in-window dual view uses.
//
// That separation is exactly what makes the pop-out legal in the first place. The single-instance
// invariant (#1119 — a second `WebViewer` permanently breaks the first instance's click-to-select)
// is a statement about one JS realm: Apryse's globals are per-realm, so two instances in two
// documents cannot collide the way two in one document do.
//
// So requirement 3's "must share the same cache" is met the only way it CAN be met across realms:
// the two caches are reconciled through the SERVER, not through each other. Whenever either window
// completes a mutation it broadcasts a note, and the other window invalidates its quotation queries
// and refetches. No entity payloads ever cross the channel — see `TSyncHint` below for the one
// exception, which is deliberately never anything more than a routing address.
//
// Sending state across would reintroduce precisely the bug requirement 3 exists to prevent: a
// window committing a PATCH with a `version` it learned from a peer rather than from its own
// authoritative fetch. Refetching keeps the DRF optimistic-lock guarantee intact, because each
// window still reads every version from its own cache, which came from the server.

export const QUOTATION_SYNC_CHANNEL = "asq-quotation-sync"

// A hint about WHICH entity a peer's mutation touched and HOW, so the receiving window can apply a
// single targeted AnnotationService call instead of a full-document rebuild — see the header above
// `applyTargetedHints` for the full rationale and the safety rule this type exists to enforce.
export type TSyncHint =
	| { entityType: "furniture"; entityId: number; kind: "status" }
	| { entityType: "furniture"; entityId: number; kind: "coordinates" }
	| { entityType: "furniture"; entityId: number; kind: "fields" }
	| { entityType: "eref"; entityId: number; kind: "status" }
	| { entityType: "eref"; entityId: number; kind: "coordinates" }
	| { entityType: "eref"; entityId: number; kind: "fields" }
	| { entityType: "ecall"; entityId: number; kind: "coordinates" }
	| { entityType: "ecall"; entityId: number; kind: "fields" }

// `hints` is OPTIONAL and, when present, is a best-effort ROUTING address only — never entity data.
// The receiving window still reads every value (status, coordinates) from its own freshly-refetched
// cache before touching the canvas; see the "writes nothing into the cache" test this type must
// keep passing. Absent (or if applying it fails) means "fall back to the full rebuild", which stays
// correct-by-construction for every mutation this file doesn't know how to fast-path.
export type TQuotationSyncMessage = { type: "quotation-mutated"; hints?: TSyncHint[] }

// Rapid mutation bursts (a batch status change, a multi-delete) would otherwise broadcast per
// mutation and make the peer refetch the whole quotation several times over. One trailing note per
// burst is enough. Kept short, since for a single edit it is pure latency. Queue-serialised chains
// (create → name → price) outlast it and send one note per step; the receiver copes with that.
export const BROADCAST_DEBOUNCE_MS = 40

// Prefix-matched, so these cover ["quotation", id], ["quotations", drawingId] and
// [CUSTOM_MODEL_NUMBERS_QUERY_KEY, drawingId].
//
// The custom-model-number key is not optional cosmetics: those definitions are DRAWING-LOCAL
// (#1345, landed after the pop-out PoC) and are created from inside the editor's model-number
// picker, so a definition added in one window is immediately pickable in the other — without this
// prefix the peer's picker would keep showing a stale list until it was reopened.
const QUOTATION_DETAIL_QUERY_KEY = ["quotation"]
const SYNCED_QUERY_KEYS = [QUOTATION_DETAIL_QUERY_KEY, ["quotations"], [CUSTOM_MODEL_NUMBERS_QUERY_KEY]]

const isSupported = () => typeof BroadcastChannel !== "undefined"

// The slice of TanStack Query's `Mutation` this file actually reads. Loosely typed on purpose —
// this only ever runs against the live MutationCache event, never against test fixtures pretending
// to be one, so there's nothing to gain from importing the library's own (much larger) type here.
type TObservedMutation = {
	options: { mutationKey?: readonly unknown[] }
	state: { variables: unknown; data: unknown }
}

// Every PATCH this file fast-paths sends `{ version, ...coords }` and NOTHING else — that exact,
// exclusive shape is what tells a coordinate-only commit (useCoordinateCommit.ts) apart from every
// OTHER edit these same mutations carry (dimensions, delivery date, comment, ...), which share the
// same mutationKey. A body with any other field present is some other kind of edit, not a move.
const isCoordinatesOnlyBody = (body: unknown): body is TCoordinates & { version: number } => {
	if (!body || typeof body !== "object") return false
	const keys = Object.keys(body).sort().join(",")
	return keys === "version,x0,x1,y0,y1"
}

const hasNoCoordinates = (body: unknown): boolean =>
	!!body && typeof body === "object" && !["x0", "y0", "x1", "y1"].some((key) => key in body)

// A text-only eref/ecall edit: no geometry and no page move (both re-place the annotation).
const isTextOnlyBody = (body: unknown): boolean =>
	hasNoCoordinates(body) && !("page_number" in (body as object))

// Renaming or moving an eref can unlink ecalls; those ecalls' links need the full rebuild's re-derivation.
const hasDisconnectedEcalls = (data: unknown): boolean =>
	!!(data as { disconnected_ecalls?: unknown[] } | undefined)?.disconnected_ecalls?.length

// Resolve a successful mutation into a sync hint, using ONLY information the mutation already
// carries (its `mutationKey`, its own request variables, its own response) — never a new payload
// invented for this purpose. Every mutation hook in quotations.hook.ts already declares a
// `mutationKey`, so this needs no per-call-site opt-in; it only has to recognise the handful whose
// shape it knows. Anything it doesn't recognise returns `null`, which the caller treats as "hint
// coverage lost for this batch" — silently mis-routing an edit would be worse than the full rebuild
// this is meant to avoid, so an unknown mutation always wins the fallback.
export const extractSyncHint = (mutation: TObservedMutation): TSyncHint | null => {
	const key = mutation.options.mutationKey?.[0]
	const vars = mutation.state.variables as
		| { furnitureId?: number; erefId?: number; ecallId?: number; body?: unknown }
		| undefined
	if (!vars) return null

	switch (key) {
		case "updateFurnitureStatus":
			return vars.furnitureId != null ? { entityType: "furniture", entityId: vars.furnitureId, kind: "status" } : null
		case "updateErefStatus":
			return vars.erefId != null ? { entityType: "eref", entityId: vars.erefId, kind: "status" } : null
		case "updateFurniture":
			if (vars.furnitureId == null) return null
			if (isCoordinatesOnlyBody(vars.body)) return { entityType: "furniture", entityId: vars.furnitureId, kind: "coordinates" }
			return hasNoCoordinates(vars.body) ? { entityType: "furniture", entityId: vars.furnitureId, kind: "fields" } : null
		// Single-object field edits that leave geometry, connectivity and page untouched — only the
		// label (and, defensively, the status fill) can change, so one annotation refresh covers them.
		case "updateFurnitureConfig":
		case "updateFurnitureComment":
		case "deleteFurnitureComment":
		case "updateFurnitureCustomization":
		case "deleteFurnitureCustomization":
		case "updateFurnitureFixedPrice":
		case "updateFurnitureCountertopSeries":
			return vars.furnitureId != null ? { entityType: "furniture", entityId: vars.furnitureId, kind: "fields" } : null
		case "updateEref": {
			if (vars.erefId == null) return null
			if (!isCoordinatesOnlyBody(vars.body)) {
				return isTextOnlyBody(vars.body) && !hasDisconnectedEcalls(mutation.state.data)
					? { entityType: "eref", entityId: vars.erefId, kind: "fields" }
					: null
			}
			// Moving an eref can DISCONNECT ecalls that were previously connected to it (a page change,
			// most often) — the response then carries `disconnected_ecalls`/`disconnected_ecall_elevations`
			// that a single `applyEntityCoordinates` call has no way to also fan out to. Ceding to the
			// full rebuild here is deliberate, not a gap to close later: those ecalls need their own
			// connectivity re-derived, which is exactly what the full rebuild already does correctly.
			if (hasDisconnectedEcalls(mutation.state.data)) return null
			return { entityType: "eref", entityId: vars.erefId, kind: "coordinates" }
		}
		case "updateEcall":
			if (vars.ecallId == null) return null
			if (isCoordinatesOnlyBody(vars.body)) return { entityType: "ecall", entityId: vars.ecallId, kind: "coordinates" }
			// Erefs it unlinks (a page-name change on a linked ecall) need no refresh of their own: an
			// eref's fill depends only on its furniture, and the ecall's own links come from its fresh row.
			return isTextOnlyBody(vars.body) ? { entityType: "ecall", entityId: vars.ecallId, kind: "fields" } : null
		// Eref estimation settings — nothing on the canvas reads them, so the refresh is a formality.
		case "updateErefConfiguration":
		case "updateErefComment":
		case "deleteErefComment":
			return vars.erefId != null ? { entityType: "eref", entityId: vars.erefId, kind: "fields" } : null
		default:
			return null
	}
}

// Find the freshest value of one hinted entity across every quotation this window has open (in
// practice exactly one). Reads directly from the QueryClient's own cache — the same cache
// `invalidateFromPeer`'s refetch just populated — never from the hint itself, which carries no
// entity data by design.
const findFreshEntity = (queryClient: QueryClient, hint: TSyncHint): TFurniture | TEref | TEcall | undefined => {
	for (const query of queryClient.getQueryCache().findAll({ queryKey: ["quotation"] })) {
		const detail = (query.state.data as AxiosResponse<TQuotationDetail> | undefined)?.data
		if (!detail) continue
		for (const page of detail.related_objects) {
			const list = hint.entityType === "furniture" ? page.furniture : hint.entityType === "eref" ? page.erefs : page.ecalls
			const match = list.find((entity) => entity.id === hint.entityId)
			if (match) return match
		}
	}
	return undefined
}

// Apply every hint as a single targeted AnnotationService call, reusing the exact methods the
// LOCAL window already trusts for its own optimistic updates (updateFurnitureEntityStatus,
// applyEntityCoordinates, ...) — this file just points them at the peer's edit instead. Returns
// false the moment any hint can't be resolved (entity missing from the fresh cache, most likely a
// delete this file doesn't fast-path), which tells the caller to fall back to the full rebuild
// rather than leave the canvas partially stale.
const applyTargetedHints = (queryClient: QueryClient, hints: TSyncHint[]): boolean => {
	for (const hint of hints) {
		const entity = findFreshEntity(queryClient, hint)
		if (!entity) return false
		// Not drawn here yet (a create→PATCH chain whose create this window never painted) — only the
		// full rebuild adds it; the in-place updaters would silently do nothing.
		if (!AnnotationService.hasEntityAnnotation(hint.entityType, hint.entityId)) return false

		if (hint.kind === "fields") {
			if (hint.entityType === "furniture") {
				const furniture = entity as TFurniture
				AnnotationService.updateFurnitureAnnotation(furniture)
				AnnotationService.updateFurnitureEntityStatus(furniture.id, furniture.status ?? "work_in_progress")
			} else if (hint.entityType === "eref") AnnotationService.updateErefAnnotationFields(entity as TEref)
			else AnnotationService.updateEcallAnnotationFields(entity as TEcall)
		} else if (hint.kind === "status") {
			const status = (entity as TFurniture | TEref).status ?? "work_in_progress"
			if (hint.entityType === "furniture") AnnotationService.updateFurnitureEntityStatus(hint.entityId, status)
			else AnnotationService.updateErefEntityStatus(hint.entityId, status)
		} else {
			AnnotationService.applyEntityCoordinates(hint.entityType, hint.entityId, entity.coordinates)
		}
	}
	return true
}

// Wire a window into the sync: broadcast this window's successful mutations, and refetch when the
// peer reports one of its own. Returns a cleanup that fully detaches. Safe to call in a window that
// will never open a peer — with nobody on the channel it costs one idle BroadcastChannel.
export const startQuotationCacheSync = (queryClient: QueryClient): (() => void) => {
	if (!isSupported()) return () => {}

	const channel = new BroadcastChannel(QUOTATION_SYNC_CHANNEL)
	let debounceTimer: number | null = null
	let pendingHints: TSyncHint[] = []
	// Set the moment ANY mutation in the current burst can't be turned into a hint — one unrouted
	// edit is enough to make the whole burst's coverage untrustworthy.
	let hintCoverageLost = false

	// Receiver side. A peer message landing while the previous one's refetch is in flight CANCELS that
	// refetch (`cancelRefetch` defaults to true), whose promise then resolves with the stale cache. So
	// hints accumulate here and only the LATEST refetch — which started after every mutation reported
	// so far — applies them; an earlier, superseded one does nothing.
	let receivedHints: TSyncHint[] = []
	let receivedCoverageLost = false
	let refetchGeneration = 0

	const invalidateFromPeer = (hints: TSyncHint[] | undefined) => {
		if (hints?.length) receivedHints.push(...hints)
		else receivedCoverageLost = true
		const generation = ++refetchGeneration

		// Invalidate first, always — even the fast path reads versions from THIS window's own
		// authoritative refetch. Only the detail query gates the canvas; the list and custom-model-number
		// refetches run alongside without being waited on.
		const detailRefetch = queryClient.invalidateQueries({ queryKey: QUOTATION_DETAIL_QUERY_KEY })
		for (const queryKey of SYNCED_QUERY_KEYS) {
			if (queryKey !== QUOTATION_DETAIL_QUERY_KEY) void queryClient.invalidateQueries({ queryKey })
		}
		// Fire-and-forget: a failed refetch surfaces via the query's own error state, and its hints stay
		// queued for the next message.
		void detailRefetch.then(
			() => {
				if (generation !== refetchGeneration) return
				const toApply = receivedHints
				const coverageLost = receivedCoverageLost
				receivedHints = []
				receivedCoverageLost = false
				if (!coverageLost && toApply.length && applyTargetedHints(queryClient, toApply)) return
				useQuotationPeerSyncStore.getState().bumpPeerUpdateEpoch()
			},
			() => {},
		)
	}

	channel.onmessage = (event: MessageEvent<TQuotationSyncMessage>) => {
		if (event.data?.type === "quotation-mutated") invalidateFromPeer(event.data.hints)
	}

	const broadcast = () => {
		debounceTimer = null
		const hints = hintCoverageLost ? undefined : pendingHints.length > 0 ? pendingHints : undefined
		pendingHints = []
		hintCoverageLost = false
		// `hints` omitted entirely (not sent as `undefined`) when absent, so a receiver on an older
		// build — or a test asserting the bare shape — sees exactly `{ type: "quotation-mutated" }`.
		const message: TQuotationSyncMessage = hints ? { type: "quotation-mutated", hints } : { type: "quotation-mutated" }
		// A window whose peer has already gone is the normal case, not an error — the channel simply
		// has no other listener. `postMessage` on a closed channel throws, so guard the teardown race.
		try {
			channel.postMessage(message)
		} catch {
			// Channel already closed by cleanup; nothing to tell anyone.
		}
	}

	const scheduleBroadcast = () => {
		if (debounceTimer != null) window.clearTimeout(debounceTimer)
		debounceTimer = window.setTimeout(broadcast, BROADCAST_DEBOUNCE_MS)
	}

	// Hooked on MutationCache (every write passes through it) rather than `state.status`, which
	// stays "success" forever and caused a live cross-window refetch loop (#1430 QA finding) —
	// `updated`/`success` is the one event that means "succeeded right now".
	const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
		if (event.type !== "updated" || event.action.type !== "success") return
		const hint = extractSyncHint(event.mutation)
		if (hint) pendingHints.push(hint)
		else hintCoverageLost = true
		scheduleBroadcast()
	})

	return () => {
		unsubscribe()
		if (debounceTimer != null) window.clearTimeout(debounceTimer)
		channel.onmessage = null
		channel.close()
	}
}
